import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { HttpService } from 'core/services/http';
import SelectCategory from './SelectCategory';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider,
    Chip,
    Stack,
    Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Utils } from 'core/utils';

const EXPERIENCE_LEVELS = [
    { value: 'Expert', label: 'Expert' },
    { value: 'Senior', label: 'Senior' },
    { value: 'Junior', label: 'Junior' },
    { value: 'Regular', label: 'Regular' },
    { value: 'Internship', label: 'Internship' },
    { value: 'Associate', label: 'Associate' }
];

function JobsFilterSidebar({ filters, onFilterChange, onApplyFilters, onResetFilters, appliedFilters, onRemoveFilter }) {
    const [localFilters, setLocalFilters] = useState(filters || {
        category: '',
        country: '',
        city: '',
        jobtype: '',
        schedule: '',
        experience_level: '',
        pay_min: '',
        pay_max: ''
    });
    const [salaryRange, setSalaryRange] = useState([0, 50000]);

    // Sync local filters when filters prop changes
    React.useEffect(() => {
        if (filters) {
            setLocalFilters(filters);
        }
    }, [filters]);

    // Sync local filters when applied filters are removed
    React.useEffect(() => {
        if (appliedFilters) {
            // Reset local filters that are not in applied filters
            setLocalFilters(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(key => {
                    if (!appliedFilters[key] && key !== 'pay_min' && key !== 'pay_max') {
                        updated[key] = '';
                    }
                });
                return updated;
            });
        }
    }, [appliedFilters]);

    // Load filter options
    const { data: categoriesData } = useQuery('categories', async () => {
        const service = new HttpService();
        const data = await service.get('/job/category/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    }, { staleTime: 5 * 60 * 1000 });

    const categories = React.useMemo(() => {
        const cats = categoriesData?.results || [];
        return Array.isArray(cats) ? cats : (Array.isArray(categoriesData) ? categoriesData : []);
    }, [categoriesData]);

    const { data: countries } = useQuery('countries', async () => {
        const service = new HttpService();
        const data = await service.get('/base/country/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    }, {
        staleTime: 24 * 60 * 60 * 1000, // 24 hours - countries rarely change
        cacheTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const { data: jobTypesData } = useQuery('jobTypes', async () => {
        const service = new HttpService();
        const data = await service.get('/job/jobtype/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    });
    
    const jobTypes = Array.isArray(jobTypesData) ? jobTypesData : (jobTypesData?.results || []);

    const { data: schedulesData } = useQuery('schedules', async () => {
        const service = new HttpService();
        const data = await service.get('/job/schedule/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    });
    
    const schedules = Array.isArray(schedulesData) ? schedulesData : (schedulesData?.results || []);

    const handleLocalFilterChange = (name, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleApply = () => {
        const cleanedFilters = Object.entries(localFilters).reduce((acc, [key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});
        
        if (salaryRange[0] > 0) cleanedFilters.pay_min = salaryRange[0];
        if (salaryRange[1] < 50000) cleanedFilters.pay_max = salaryRange[1];
        
        if (onApplyFilters) {
            onApplyFilters(cleanedFilters);
        }
    };

    const handleReset = () => {
        setLocalFilters({
            category: '',
            country: '',
            city: '',
            jobtype: '',
            schedule: '',
            experience_level: '',
            pay_min: '',
            pay_max: ''
        });
        setSalaryRange([0, 50000]);
        if (onResetFilters) {
            onResetFilters();
        }
    };

    const activeFiltersCount = Object.keys(appliedFilters || {}).length;

    return (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, position: 'sticky', top: 80, maxHeight: 'calc(100vh - 100px)', overflowY: 'hidden', overflowX: 'hidden' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight={600}>
                    Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                </Typography>
                {activeFiltersCount > 0 && (
                    <Button size="small" onClick={handleReset} sx={{ textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0 }}>
                        Clear all
                    </Button>
                )}
            </Box>

            <Stack spacing={2}>
                {/* Category */}
                <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={localFilters.category || ''}
                        label="Category"
                        onChange={(e) => handleLocalFilterChange('category', e.target.value)}
                    >
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                                {Utils.normalizeText(cat.title)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Country */}
                <FormControl fullWidth size="small">
                    <InputLabel>Country</InputLabel>
                    <Select
                        value={localFilters.country || ''}
                        label="Country"
                        onChange={(e) => handleLocalFilterChange('country', e.target.value)}
                    >
                        <MenuItem value="">All Countries</MenuItem>
                        {countries?.results?.map((country) => (
                            <MenuItem key={country.id} value={country.id}>
                                {Utils.getDefaultName(country)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* City */}
                <TextField
                    fullWidth
                    size="small"
                    label="City"
                    value={localFilters.city || ''}
                    onChange={(e) => handleLocalFilterChange('city', e.target.value)}
                />

                {/* Job Type */}
                <FormControl fullWidth size="small">
                    <InputLabel>Job Type</InputLabel>
                    <Select
                        value={localFilters.jobtype || ''}
                        label="Job Type"
                        onChange={(e) => handleLocalFilterChange('jobtype', e.target.value)}
                    >
                        <MenuItem value="">All Job Types</MenuItem>
                        {jobTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                                {Utils.normalizeText(type.title)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Schedule */}
                <FormControl fullWidth size="small">
                    <InputLabel>Schedule</InputLabel>
                    <Select
                        value={localFilters.schedule || ''}
                        label="Schedule"
                        onChange={(e) => handleLocalFilterChange('schedule', e.target.value)}
                    >
                        <MenuItem value="">All Schedules</MenuItem>
                        {schedules.map((schedule) => (
                            <MenuItem key={schedule.id} value={schedule.id}>
                                {Utils.normalizeText(schedule.title)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Experience Level */}
                <FormControl fullWidth size="small">
                    <InputLabel>Experience Level</InputLabel>
                    <Select
                        value={localFilters.experience_level || ''}
                        label="Experience Level"
                        onChange={(e) => handleLocalFilterChange('experience_level', e.target.value)}
                    >
                        <MenuItem value="">All Levels</MenuItem>
                        {EXPERIENCE_LEVELS.map((level) => (
                            <MenuItem key={level.value} value={level.value}>
                                {level.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Salary Range */}
                <Box>
                    <Typography variant="body2" gutterBottom>
                        Salary Range: ${salaryRange[0].toLocaleString()} – ${salaryRange[1].toLocaleString()}
                    </Typography>
                    <Box sx={{ px: 1 }}>
                        <Slider
                            value={salaryRange}
                            onChange={(e, newValue) => setSalaryRange(newValue)}
                            valueLabelDisplay="auto"
                            min={0}
                            max={50000}
                            step={1000}
                        />
                    </Box>
                </Box>

                <Divider />

                {/* Action Buttons */}
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        fullWidth
                        size="small"
                        disableElevation
                        onClick={handleApply}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Apply
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        onClick={handleReset}
                        sx={{ textTransform: 'none' }}
                    >
                        Reset
                    </Button>
                </Stack>

                {/* Active Filters */}
                {activeFiltersCount > 0 && (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Active Filters:
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {Object.entries(appliedFilters).map(([key, value]) => {
                                let label = `${key}: ${value}`;
                                if (key === 'category' && value) {
                                    const category = categories?.find(c => String(c.id) === String(value));
                                    label = category ? Utils.normalizeText(category.title) : value;
                                } else if (key === 'country' && value) {
                                    const country = countries?.results?.find(c => String(c.id) === String(value));
                                    label = country ? Utils.getDefaultName(country) : value;
                                } else if (key === 'jobtype' && value) {
                                    const jobType = jobTypes?.find(jt => String(jt.id) === String(value));
                                    label = jobType ? Utils.normalizeText(jobType.title) : value;
                                } else if (key === 'schedule' && value) {
                                    const schedule = schedules?.find(s => String(s.id) === String(value));
                                    label = schedule ? Utils.normalizeText(schedule.title) : value;
                                } else if (key === 'experience_level' && value) {
                                    const level = EXPERIENCE_LEVELS.find(el => el.value === value);
                                    label = level ? level.label : value;
                                } else if (key === 'pay_min') {
                                    label = `Min: $${value}`;
                                } else if (key === 'pay_max') {
                                    label = `Max: $${value}`;
                                }
                                return (
                                    <Chip
                                        key={key}
                                        label={label}
                                        onDelete={() => onRemoveFilter && onRemoveFilter(key)}
                                        size="small"
                                        sx={{ mb: 0.5 }}
                                    />
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Paper>
    );
}

export default JobsFilterSidebar;
